"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Switch } from "@/app/_module/components/ui/switch";
import { Badge } from "@/app/_module/components/ui/badge";
import { Trophy, Globe, Users, Eye, Loader2, MessageSquareOff, UserMinus } from "lucide-react";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import api from "@/app/_module/services/api";
import { useToast } from "@/app/_module/components/ui/use-toast";

/**
 * Agency Center leaderboard + chat settings panel.
 *
 * Bundles three independent boolean controls into one card so the owner
 * has a single place to manage who-sees-what:
 *
 *   1. Leaderboard Enabled (leaderboard_enabled column)
 *      Master kill-switch. When off, NO agents in this agency see the
 *      leaderboard menu at all.
 *
 *   2. Agency-Wide Mode (agency_wide_enabled column)
 *      MUTUAL consent for sharing with neighbors. Both this agency AND
 *      the sub-agency must have it on for them to merge leaderboards +
 *      team chat. Sub-agencies see each other through this.
 *
 *   3. Owner Full Downline View (parent_godmode_enabled column)
 *      Privileged owner-only mode. When on, the owner can switch into
 *      "Full Downline" scope on the leaderboard to see every sub-agency
 *      regardless of consent + every agent including ones who hid
 *      themselves. ONE-WAY — sub-agencies don't see anything different.
 *
 * All three back-end via the SAME endpoint
 * (/agencies/:id/leaderboard-settings) — keeping this widget in sync
 * with the equivalent Settings page panel automatically. Flip a switch
 * here and the Settings page reads the same row.
 */
export default function AgencyWideToggle() {
  const { user } = useUserContext();
  const { toast } = useToast();
  const [leaderboardEnabled, setLeaderboardEnabled] = useState(true);
  const [agencyWideEnabled, setAgencyWideEnabled] = useState(false);
  const [parentGodmodeEnabled, setParentGodmodeEnabled] = useState(false);
  const [forceSubAgencyInclusion, setForceSubAgencyInclusion] = useState(false);
  // Sub-gates that live underneath Agency-Wide Mode. Default false to
  // preserve existing behavior. Owners flip them ON to lock down chat
  // when their sub-agencies share the pool.
  const [disableCrossAgencyDm, setDisableCrossAgencyDm] = useState(false);
  const [disableMemberView, setDisableMemberView] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<
    | null
    | 'leaderboardEnabled'
    | 'agencyWideEnabled'
    | 'parentGodmodeEnabled'
    | 'forceSubAgencyInclusion'
    | 'disableCrossAgencyDm'
    | 'disableMemberView'
  >(null);

  const agencyId = AgencyTokenManager.getCurrentAgencyId() || user?.currentAgency?.agency?.id;
  const role = user?.currentAgency?.role?.toLowerCase?.() || "";
  const isOwnerOrAdmin = ["owner", "admin", "agency_owner"].includes(role);
  // Only the actual agency owner can flip god-mode. The endpoint also
  // re-checks; the disabled state here is a friendly UI hint only.
  const isOwner = ["owner", "agency_owner"].includes(role);

  const fetchSettings = useCallback(async () => {
    if (!agencyId) return;
    try {
      const res = await api.get({
        url: getApiUrl(endpoint.agency.getLeaderboardSettings(agencyId)),
        auth: true,
      });
      const data = (res as any)?.data?.data ?? (res as any)?.data ?? res;
      setLeaderboardEnabled(data?.leaderboardEnabled !== false);
      setAgencyWideEnabled(data?.agencyWideEnabled === true);
      setParentGodmodeEnabled(data?.parentGodmodeEnabled === true);
      setForceSubAgencyInclusion(data?.forceSubAgencyInclusion === true);
      setDisableCrossAgencyDm(data?.disableCrossAgencyDm === true);
      setDisableMemberView(data?.disableMemberView === true);
    } catch {
      // Defaults are fine on failure — endpoint may not be wired yet on
      // some agencies (very old rows). Don't surface an error toast for
      // a silent read.
    } finally {
      setLoading(false);
    }
  }, [agencyId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  /**
   * Single PUT for any of the three toggles. The endpoint accepts a partial
   * body — fields you omit stay untouched on the row. We optimistically
   * update local state, then revert on failure.
   */
  const persistToggle = async (
    key:
      | 'leaderboardEnabled'
      | 'agencyWideEnabled'
      | 'parentGodmodeEnabled'
      | 'forceSubAgencyInclusion'
      | 'disableCrossAgencyDm'
      | 'disableMemberView',
    value: boolean,
    label: string,
    onMessage: string,
    offMessage: string,
  ) => {
    if (!agencyId || savingKey) return;
    setSavingKey(key);
    const prev =
      key === 'leaderboardEnabled' ? leaderboardEnabled
      : key === 'agencyWideEnabled' ? agencyWideEnabled
      : key === 'parentGodmodeEnabled' ? parentGodmodeEnabled
      : key === 'forceSubAgencyInclusion' ? forceSubAgencyInclusion
      : key === 'disableCrossAgencyDm' ? disableCrossAgencyDm
      : disableMemberView;
    const setter =
      key === 'leaderboardEnabled' ? setLeaderboardEnabled
      : key === 'agencyWideEnabled' ? setAgencyWideEnabled
      : key === 'parentGodmodeEnabled' ? setParentGodmodeEnabled
      : key === 'forceSubAgencyInclusion' ? setForceSubAgencyInclusion
      : key === 'disableCrossAgencyDm' ? setDisableCrossAgencyDm
      : setDisableMemberView;
    setter(value);
    try {
      await api.put({
        url: getApiUrl(endpoint.agency.updateLeaderboardSettings(agencyId)),
        body: { [key]: value },
        auth: true,
      });
      toast({
        title: value ? `${label} Enabled` : `${label} Disabled`,
        description: value ? onMessage : offMessage,
      });
    } catch (err: any) {
      setter(prev);
      toast({
        title: 'Failed to update',
        description: err?.response?.data?.message || 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setSavingKey(null);
    }
  };

  if (!isOwnerOrAdmin) return null;

  return (
    <Card className="border border-gray-200 shadow-sm">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-600" />
          <h3 className="text-sm font-semibold text-gray-900">Leaderboard & Chat</h3>
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-300 ml-auto" />}
        </div>

        {/* Toggle 1 — Master kill-switch */}
        <ToggleRow
          icon={<Trophy className="w-4 h-4 text-amber-600" />}
          title="Leaderboard Enabled"
          description={
            leaderboardEnabled
              ? "Agents in this agency can view and participate in the leaderboard."
              : "Disabled. No agents in this agency can see the leaderboard at all."
          }
          checked={leaderboardEnabled}
          saving={savingKey === 'leaderboardEnabled'}
          disabled={!!savingKey && savingKey !== 'leaderboardEnabled'}
          onChange={(v) =>
            persistToggle(
              'leaderboardEnabled',
              v,
              'Leaderboard',
              'Agents in this agency can now view the leaderboard.',
              'Agents in this agency can no longer see the leaderboard.',
            )
          }
        />

        {/* Toggle 2 — Agency-Wide (mutual consent) */}
        <ToggleRow
          icon={<Globe className="w-4 h-4 text-blue-600" />}
          title="Agency-Wide Mode"
          activeBadge={agencyWideEnabled ? <Badge variant="secondary" className="bg-blue-50 text-blue-700 text-[10px] font-medium">Active</Badge> : null}
          description={
            agencyWideEnabled
              ? "Sharing leaderboard + team chat with parent/sub-agencies that also have this on. Both sides must opt in."
              : "Leaderboard + chat are isolated to this agency only."
          }
          footerNote={
            agencyWideEnabled ? (
              <>
                <div className="flex items-center gap-1.5 mt-2">
                  <Users className="w-3 h-3 text-gray-400" />
                  <span className="text-[11px] text-gray-400">
                    Agents from all opted-in agencies show up here.
                  </span>
                </div>
                <p className="text-[11px] text-red-500 font-medium mt-1.5">
                  Only within your agency hierarchy — nothing is shared outside your parent tree.
                </p>
                {/* Sub-toggle: force sub-agencies in without their consent.
                    Only shown when Agency-Wide is on, because that's the
                    only context where "force" makes sense. Behaves like
                    consent-mutual agency-wide for all agents (public, not
                    owner-only like god-mode). */}
                {isOwner && (
                  <div className="mt-3 pt-3 border-t border-gray-200 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-900">Force-include all sub-agencies</span>
                        {forceSubAgencyInclusion && (
                          <Badge variant="secondary" className="bg-orange-100 text-orange-800 text-[10px] font-medium">Forcing</Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">
                        {forceSubAgencyInclusion
                          ? "Every sub-agency below you is in the agency-wide pool — even ones that haven't toggled it on. All their agents see the merged view too."
                          : "Off — sub-agencies have to toggle Agency-Wide on themselves to be included (standard mutual consent)."}
                      </p>
                      <p className="text-[11px] text-orange-700 mt-1.5">
                        Strong move: overrides each sub-agency's own consent. They didn't choose to share — you chose for them.
                      </p>
                    </div>
                    <div className="shrink-0 pt-0.5">
                      {savingKey === 'forceSubAgencyInclusion' ? (
                        <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
                      ) : (
                        <Switch
                          checked={forceSubAgencyInclusion}
                          onCheckedChange={(v) =>
                            persistToggle(
                              'forceSubAgencyInclusion',
                              v,
                              'Force-include sub-agencies',
                              'All sub-agencies below you are now in the agency-wide pool, regardless of their own toggle.',
                              'Reverted to standard mutual-consent agency-wide.',
                            )
                          }
                          // We're already in the else-branch of the
                          // savingKey === 'forceSubAgencyInclusion' check
                          // above (would have rendered the Loader), so a
                          // truthy savingKey here means a different toggle
                          // is saving.
                          disabled={!!savingKey}
                          className="data-[state=checked]:bg-orange-600"
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Sub-gate: disable cross-agency DMs.
                    Stops agents from DMing users in other agencies of the
                    pool without unwinding the leaderboard or shared
                    channels. Anti-poaching / anti-mass-DM control. */}
                <div className="mt-3 pt-3 border-t border-gray-200 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <MessageSquareOff className="w-3.5 h-3.5 text-rose-600" />
                      <span className="text-xs font-semibold text-gray-900">Disable cross-agency DMs</span>
                      {disableCrossAgencyDm && (
                        <Badge variant="secondary" className="bg-rose-100 text-rose-800 text-[10px] font-medium">Locked</Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">
                      {disableCrossAgencyDm
                        ? "Agents in this agency can only DM users in their own agency. Cross-agency DMs are blocked even though shared channels stay on."
                        : "Off — agents can DM anyone in the agency-wide pool (default)."}
                    </p>
                    <p className="text-[11px] text-rose-700 mt-1.5">
                      Recommended when sub-agencies share the pool. Stops outsiders from cold-messaging your agents.
                    </p>
                  </div>
                  <div className="shrink-0 pt-0.5">
                    {savingKey === 'disableCrossAgencyDm' ? (
                      <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
                    ) : (
                      <Switch
                        checked={disableCrossAgencyDm}
                        onCheckedChange={(v) =>
                          persistToggle(
                            'disableCrossAgencyDm',
                            v,
                            'Cross-agency DMs',
                            'Cross-agency DMs are now blocked. Agents in this agency can only DM their own agency.',
                            'Cross-agency DMs re-enabled.',
                          )
                        }
                        disabled={!!savingKey}
                        className="data-[state=checked]:bg-rose-600"
                      />
                    )}
                  </div>
                </div>

                {/* Sub-gate: disable member directory view.
                    Stops the Discord-style "see who's in this room" roster.
                    Shared messages still appear; only the sidebar member
                    list is filtered down to same-agency users. */}
                <div className="mt-3 pt-3 border-t border-gray-200 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <UserMinus className="w-3.5 h-3.5 text-rose-600" />
                      <span className="text-xs font-semibold text-gray-900">Hide cross-agency member list</span>
                      {disableMemberView && (
                        <Badge variant="secondary" className="bg-rose-100 text-rose-800 text-[10px] font-medium">Hidden</Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">
                      {disableMemberView
                        ? "Agents in this agency see only their own agency's users in channel member sidebars. Cross-agency messages still appear in the feed."
                        : "Off — agents see every user in shared channels (default)."}
                    </p>
                    <p className="text-[11px] text-rose-700 mt-1.5">
                      Stops agents from scraping the cross-agency roster.
                    </p>
                  </div>
                  <div className="shrink-0 pt-0.5">
                    {savingKey === 'disableMemberView' ? (
                      <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
                    ) : (
                      <Switch
                        checked={disableMemberView}
                        onCheckedChange={(v) =>
                          persistToggle(
                            'disableMemberView',
                            v,
                            'Cross-agency member list',
                            'Cross-agency users are now hidden from channel member sidebars.',
                            'Cross-agency users will appear in channel member sidebars again.',
                          )
                        }
                        disabled={!!savingKey}
                        className="data-[state=checked]:bg-rose-600"
                      />
                    )}
                  </div>
                </div>
              </>
            ) : null
          }
          checked={agencyWideEnabled}
          saving={savingKey === 'agencyWideEnabled'}
          disabled={!!savingKey && savingKey !== 'agencyWideEnabled'}
          onChange={(v) =>
            persistToggle(
              'agencyWideEnabled',
              v,
              'Agency-Wide Mode',
              'Leaderboard and team chat will merge with parent/sub-agencies that also have this on.',
              'Leaderboard and team chat are now isolated to this agency only.',
            )
          }
        />

        {/* Toggle 3 — Parent god-mode (owner-only) */}
        <ToggleRow
          icon={<Eye className="w-4 h-4 text-violet-600" />}
          title="Owner Full Downline View"
          activeBadge={
            parentGodmodeEnabled ? (
              <Badge variant="secondary" className="bg-violet-100 text-violet-800 text-[10px] font-medium">Privileged</Badge>
            ) : null
          }
          description={
            parentGodmodeEnabled
              ? "You can switch the leaderboard into Full Downline mode to see every sub-agency — even ones that haven't opted into shared leaderboards — plus every agent including those who hid themselves. Visible only to you."
              : "Off. You only see sub-agencies that opted in with you."
          }
          footerNote={
            <p className="text-[11px] text-violet-700 mt-1.5">
              Every Full Downline view is logged for audit. Use for oversight, not casual browsing.
            </p>
          }
          checked={parentGodmodeEnabled}
          saving={savingKey === 'parentGodmodeEnabled'}
          disabled={(!!savingKey && savingKey !== 'parentGodmodeEnabled') || !isOwner}
          disabledHint={!isOwner ? 'Only the agency owner can flip this.' : undefined}
          onChange={(v) =>
            persistToggle(
              'parentGodmodeEnabled',
              v,
              'Owner Full Downline View',
              "You can now switch the leaderboard into Full Downline mode from the scope picker.",
              "Full Downline mode is no longer available.",
            )
          }
        />
      </CardContent>
    </Card>
  );
}

/**
 * Single toggle row. Kept inline because it's only used by this widget
 * and abstracting it further would just add indirection.
 */
function ToggleRow(props: {
  icon: React.ReactNode;
  title: string;
  description: string;
  activeBadge?: React.ReactNode;
  footerNote?: React.ReactNode;
  checked: boolean;
  saving: boolean;
  disabled?: boolean;
  disabledHint?: string;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-100 p-3">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
          {props.icon}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-900">{props.title}</h4>
            {props.activeBadge}
          </div>
          <p className="text-xs text-gray-500 mt-1 leading-relaxed">{props.description}</p>
          {props.footerNote}
          {props.disabled && props.disabledHint && (
            <p className="text-[11px] text-gray-400 italic mt-1">{props.disabledHint}</p>
          )}
        </div>
      </div>
      <div className="shrink-0 pt-1">
        {props.saving ? (
          <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
        ) : (
          <Switch
            checked={props.checked}
            onCheckedChange={props.onChange}
            disabled={props.disabled || props.saving}
          />
        )}
      </div>
    </div>
  );
}
